const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/portal`;

export const TOKEN_KEY = "bbc_portal_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

async function request(path, { method = "GET", body, auth = true } = {}) {
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return null;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error(formatApiError(data?.detail));
    err.status = res.status;
    throw err;
  }
  return data;
}

export const portalApi = {
  register: (payload) => request("/auth/register", { method: "POST", body: payload, auth: false }),
  login: (payload) => request("/auth/login", { method: "POST", body: payload, auth: false }),
  forgotPassword: (email) =>
    request("/auth/forgot-password", { method: "POST", body: { email, origin_url: window.location.origin }, auth: false }),
  resetPassword: (token, password) =>
    request("/auth/reset-password", { method: "POST", body: { token, password }, auth: false }),
  me: () => request("/auth/me"),
  googleSession: (sessionId, attributionData) =>
    request("/auth/google/session", {
      method: "POST",
      body: { session_id: sessionId, attribution: attributionData || null },
      auth: false,
    }),
  logout: () => request("/auth/logout", { method: "POST" }),
  setPassword: (password, currentPassword) =>
    request("/auth/set-password", {
      method: "POST",
      body: { password, current_password: currentPassword || null },
    }),
  researchConsent: (optOut) => request("/research-consent", { method: "PUT", body: { opt_out: optOut } }),
  updateProfile: (payload) =>
    request("/profile", { method: "PUT", body: { ...payload, origin_url: window.location.origin } }),
  stats: () => request("/stats"),
  progress: () => request("/progress"),
  referrals: () => request("/referrals"),
  incidentLimit: () => request("/user/incident-limit"),
  aiAccess: (incidentId) => request(`/user/ai-access${incidentId ? `?incident_id=${incidentId}` : ""}`),
  upcomingSessions: () => request("/sessions/upcoming"),
  incidents: () => request("/incidents"),
  trends: () => request("/incidents/trends"),
  createIncident: (payload) => request("/incidents", { method: "POST", body: payload }),
  incident: (id) => request(`/incidents/${id}`),
  deleteIncident: (id) => request(`/incidents/${id}`, { method: "DELETE" }),
  saveDarkTriad: (id, scores) => request(`/incidents/${id}/dark-triad`, { method: "POST", body: { scores } }),
  messages: (id) => request(`/incidents/${id}/messages`),
  latestReport: () => request("/report/latest"),
  generateReport: (regenerate = false) =>
    request("/report/generate", { method: "POST", body: { regenerate, origin_url: window.location.origin } }),
  emailReport: () => request("/report/email", { method: "POST", body: { origin_url: window.location.origin } }),
  shareReport: (includeStrategies) =>
    request("/report/share", { method: "POST", body: { include_strategies: includeStrategies } }),
  revokeShare: () => request("/report/share", { method: "DELETE" }),
  sharedReport: (token) => request(`/shared-report/${token}`, { auth: false }),
  checkout: (plan, originUrl) =>
    request("/subscription/checkout", { method: "POST", body: { plan, origin_url: originUrl } }),
  manageBilling: () =>
    request("/subscription/manage", { method: "POST", body: { return_url: window.location.origin } }),
  subscriptionStatus: (sessionId) => request(`/subscription/status/${sessionId}`),
};

/** Streams the advisor reply. onDelta(textChunk) is called as tokens arrive. */
export async function streamAdvisorReply(incidentId, content, onDelta) {
  const res = await fetch(`${BASE}/incidents/${incidentId}/messages`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify({ content: content || null }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(formatApiError(data?.detail));
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let full = "";
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      const payload = JSON.parse(line.slice(5).trim());
      if (payload.error) throw new Error(payload.error);
      if (payload.delta) {
        full += payload.delta;
        onDelta(payload.delta);
      }
    }
  }
  return full;
}

export async function downloadReportPdf() {
  const res = await fetch(`${BASE}/report/pdf`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error("Could not build the PDF right now.");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "bad-boss-clinic-vulnerability-report.pdf";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function transcribeAudio(blob) {  const form = new FormData();
  form.append("file", blob, "recording.webm");
  const res = await fetch(`${BASE}/transcribe`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getToken()}` },
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(formatApiError(data?.detail));
  return data.text;
}
